/**
 * Cruscotto commerciale licenze (SuperAdmin MAE).
 *
 * Versione v1.8.0 — aggrega lato FE da:
 *   - GET /api/tenant-subscriptions?include=plan,tenant
 *   - GET /api/license-pricings?filter[isActive]=true
 *   - GET /api/license-histories?sort=-createdAt
 *
 * I KPI sono calcolati lato client (è un cruscotto interno, qualche centinaio
 * di subscription al massimo). Quando il BE esporrà un endpoint snapshot
 * dedicato, basterà sostituire la query principale.
 */
import { createFileRoute } from '@tanstack/react-router';
import {
  Alert,
  Badge,
  Card,
  Center,
  Group,
  NumberFormatter,
  ScrollArea,
  SimpleGrid,
  Skeleton,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import { AreaChart } from '@mantine/charts';
import {
  IconAlertCircle,
  IconArrowDownRight,
  IconArrowRight,
  IconArrowUpRight,
  IconCash,
  IconCoin,
  IconCrown,
  IconTrendingUp,
  IconUsersGroup,
} from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { fetchList } from '@/shared/api/jsonapi/client';
import type { LicensePricing, TenantSubscription } from '@/shared/types/api.gen';
import type { Denormalized } from '@/shared/api/jsonapi/parser';

export const Route = createFileRoute('/_app/admin/license-dashboard')({ component: AdminDashboardPage });

type SubRow = TenantSubscription & Denormalized & {
  id: string;
  plan?: { id: string; code?: string; name?: string };
  tenant?: { id: string; name?: string };
};

type PricingRow = LicensePricing & Denormalized & {
  id: string;
  plan?: { id: string; code?: string };
};

interface LicenseHistoryRow extends Denormalized {
  id: string;
  action?: string;
  details?: string | null;
  createdAt?: string;
  tenant?: { id: string; name?: string };
}

const ACTIVE_STATUSES = new Set(['ACTIVE', 'TRIAL', 'GRACE_PERIOD']);

function AdminDashboardPage() {
  const subsQ = useQuery({
    queryKey: ['admin-all-subscriptions'],
    staleTime: 5 * 60_000,
    queryFn: () => fetchList<SubRow>('tenant-subscriptions', {
      include: ['plan', 'tenant'],
      sort: ['-createdAt'],
      page: { size: 500 },
    }),
  });

  const pricingsQ = useQuery({
    queryKey: ['admin-all-pricings'],
    staleTime: 30 * 60_000,
    queryFn: () => fetchList<PricingRow>('license-pricings', {
      filter: { isActive: { eq: true } },
      include: ['plan'],
      page: { size: 500 },
    }),
  });

  const historyQ = useQuery({
    queryKey: ['admin-license-histories'],
    staleTime: 60_000,
    queryFn: () => fetchList<LicenseHistoryRow>('license-histories', {
      include: ['tenant'],
      sort: ['-createdAt'],
      page: { size: 100 },
    }),
  });

  const snap = useMemo(() => {
    const subs = subsQ.data?.data ?? [];
    const pricings = pricingsQ.data?.data ?? [];

    // Mappa pricing per (planId, currency, billingPeriod) → pricing
    const pricingByKey = new Map<string, PricingRow>();
    for (const p of pricings) {
      const planId = p.plan?.id;
      if (!planId) continue;
      const key = `${planId}|${p.currency ?? 'EUR'}|${p.billingPeriod ?? 'monthly'}`;
      // Preferiamo il più recente (validFrom max)
      const prev = pricingByKey.get(key);
      if (!prev || (p.validFrom && (!prev.validFrom || p.validFrom > prev.validFrom))) {
        pricingByKey.set(key, p);
      }
    }

    const monthlyRevenue = (sub: SubRow): number => {
      const planId = sub.plan?.id;
      if (!planId) return 0;
      const currency = sub.currency ?? 'EUR';
      const period = sub.billingPeriod ?? 'monthly';
      const pricing = pricingByKey.get(`${planId}|${currency}|${period}`);
      if (!pricing) return 0;
      const seats = sub.seats ?? 1;
      const base = pricing.basePrice ?? 0;
      const perSeat = pricing.pricePerSeat ?? 0;
      const gross = base + perSeat * seats;
      // Normalizza ad MRR: se yearly, dividi per 12
      if (period === 'yearly') return gross / 12;
      if (period === 'quarterly') return gross / 3;
      return gross;
    };

    const activeSubs = subs.filter((s) => s.status && ACTIVE_STATUSES.has(s.status));
    const trialSubs = subs.filter((s) => s.status === 'TRIAL');
    const paidSubs = subs.filter((s) => s.status === 'ACTIVE');

    const mrr = activeSubs.reduce((acc, s) => acc + monthlyRevenue(s), 0);

    // Trial → Paid: i tenant con sub ACTIVE che in passato avevano trialEndsAt non null
    const everInTrial = subs.filter((s) => s.trialEndsAt != null).length;
    const convertedFromTrial = paidSubs.filter((s) => s.trialEndsAt != null).length;
    const trialToPaidPct = everInTrial > 0 ? (convertedFromTrial / everInTrial) * 100 : 0;

    // Churn 30g
    const thirtyDaysAgo = dayjs().subtract(30, 'day');
    const churnedRecent = subs.filter((s) => s.cancelledAt && dayjs(s.cancelledAt).isAfter(thirtyDaysAgo));
    const churnDenom = activeSubs.length + churnedRecent.length;
    const churnPct = churnDenom > 0 ? (churnedRecent.length / churnDenom) * 100 : 0;

    // Trend MRR ultimi 12 mesi: sommiamo monthlyRevenue per ogni sub la cui startDate cade nel mese
    const trend: { month: string; mrr: number; newCustomers: number; churnedCustomers: number }[] = [];
    for (let i = 11; i >= 0; i--) {
      const m = dayjs().subtract(i, 'month');
      const monthStart = m.startOf('month');
      const monthEnd = m.endOf('month');
      // MRR cumulativo a fine mese: sub attive in quel mese (started prima, no cancel oppure cancel dopo)
      const activeInMonth = subs.filter((s) => {
        if (!s.startDate) return false;
        if (dayjs(s.startDate).isAfter(monthEnd)) return false;
        if (s.cancelledAt && dayjs(s.cancelledAt).isBefore(monthStart)) return false;
        if (s.endDate && dayjs(s.endDate).isBefore(monthStart)) return false;
        return true;
      });
      const monthMrr = activeInMonth.reduce((acc, s) => acc + monthlyRevenue(s), 0);
      const inMonth = (iso: string) => {
        const d = dayjs(iso);
        return d.isSameOrAfter(monthStart) && d.isSameOrBefore(monthEnd);
      };
      const newC = subs.filter((s) => s.startDate && inMonth(s.startDate)).length;
      const churnedC = subs.filter((s) => s.cancelledAt && inMonth(s.cancelledAt)).length;
      trend.push({
        month: m.format('YYYY-MM'),
        mrr: Math.round(monthMrr),
        newCustomers: newC,
        churnedCustomers: churnedC,
      });
    }

    // Delta MRR % vs mese precedente
    const lastMrr = trend.at(-1)?.mrr ?? 0;
    const prevMrr = trend.at(-2)?.mrr ?? 0;
    const mrrDeltaPct = prevMrr > 0 ? ((lastMrr - prevMrr) / prevMrr) * 100 : undefined;

    return {
      mrr: Math.round(mrr),
      mrrDeltaPct,
      activeTenants: activeSubs.length,
      activeTenantsDelta: (trend.at(-1)?.newCustomers ?? 0) - (trend.at(-1)?.churnedCustomers ?? 0),
      trialCount: trialSubs.length,
      trialToPaidPct,
      churnPct,
      churnedRecent,
      trend,
      currency: subs[0]?.currency ?? 'EUR',
    };
  }, [subsQ.data, pricingsQ.data]);

  const isLoading = subsQ.isLoading || pricingsQ.isLoading;

  if (isLoading) {
    return (
      <Stack>
        <Skeleton height={36} width="40%" />
        <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }}>
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} height={120} radius="md" />)}
        </SimpleGrid>
        <Skeleton height={260} radius="md" />
      </Stack>
    );
  }

  const noData = (subsQ.data?.data ?? []).length === 0;

  return (
    <Stack>
      <div>
        <Title order={1}>
          <IconCrown size={28} style={{ verticalAlign: 'middle', marginRight: 8 }} />
          Cruscotto commerciale
        </Title>
        <Text c="dimmed" size="sm">
          KPI cluster-wide: MRR · tenant attivi · trial-to-paid · churn. Aggregato lato client da <code>tenant-subscriptions</code> + <code>license-pricings</code>. Visibile solo a SuperAdmin Maestrale.
        </Text>
      </div>

      {noData && (
        <Alert color="orange" variant="light" icon={<IconAlertCircle size={18} />} title="Nessuna sottoscrizione registrata">
          Non ci sono ancora sottoscrizioni nel sistema. Il cruscotto inizierà a mostrare KPI reali appena verranno create le prime sub via <code>/api/tenants/&#123;id&#125;/subscribe</code>.
        </Alert>
      )}

      <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }}>
        <Kpi
          label="MRR"
          value={
            <NumberFormatter
              value={snap.mrr}
              prefix={snap.currency === 'EUR' ? '€ ' : '$ '}
              thousandSeparator="."
              decimalSeparator=","
              decimalScale={0}
            />
          }
          deltaPct={snap.mrrDeltaPct}
          icon={IconCash}
          color="green"
        />
        <Kpi
          label="Tenant attivi"
          value={snap.activeTenants}
          delta={snap.activeTenantsDelta}
          icon={IconUsersGroup}
          color="blue"
        />
        <Kpi
          label="Trial → Paid"
          value={`${snap.trialToPaidPct.toFixed(0)}%`}
          icon={IconTrendingUp}
          color="teal"
        />
        <Kpi
          label="Churn 30g"
          value={`${snap.churnPct.toFixed(1)}%`}
          deltaInverted
          icon={IconCoin}
          color="red"
        />
      </SimpleGrid>

      <Card withBorder padding="md" radius="md">
        <Group justify="space-between" mb="md">
          <Text fw={600}>Trend MRR (ultimi 12 mesi)</Text>
          {snap.trend.length > 0 && (
            <Badge variant="light" color="green">
              <NumberFormatter
                value={snap.trend.at(-1)?.mrr ?? 0}
                prefix={snap.currency === 'EUR' ? '€ ' : '$ '}
                thousandSeparator="."
              />
            </Badge>
          )}
        </Group>
        {snap.trend.length > 0 && snap.trend.some((t) => t.mrr > 0) ? (
          <AreaChart
            h={260}
            data={snap.trend.map((t) => ({ month: t.month, MRR: t.mrr }))}
            dataKey="month"
            series={[{ name: 'MRR', color: 'green.6' }]}
            curveType="monotone"
            tickLine="y"
            gridAxis="y"
            withGradient
            withTooltip
          />
        ) : (
          <Center h={260}>
            <Stack align="center" gap="xs">
              <ThemeIcon size="xl" variant="light" color="gray" radius="xl">
                <IconTrendingUp size={28} />
              </ThemeIcon>
              <Text size="sm" c="dimmed">Trend non ancora calcolabile (nessun pricing collegato alle subscription)</Text>
            </Stack>
          </Center>
        )}
      </Card>

      <Card withBorder padding={0} radius="md">
        <Group justify="space-between" p="md" pb="xs">
          <Text fw={600}>Churn recenti (30 giorni)</Text>
          <Badge variant="light" color="gray">{snap.churnedRecent.length}</Badge>
        </Group>
        <ScrollArea type="auto">
          <Table verticalSpacing="sm" horizontalSpacing="md">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Tenant</Table.Th>
                <Table.Th>Piano</Table.Th>
                <Table.Th ta="right">Mesi attivo</Table.Th>
                <Table.Th>Churn date</Table.Th>
                <Table.Th>Motivo</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {snap.churnedRecent.map((c) => {
                const months = c.startDate && c.cancelledAt
                  ? Math.max(0, dayjs(c.cancelledAt).diff(dayjs(c.startDate), 'month'))
                  : 0;
                return (
                  <Table.Tr key={c.id}>
                    <Table.Td><Text fw={500}>{c.tenant?.name ?? c.tenant?.id ?? '—'}</Text></Table.Td>
                    <Table.Td><Badge variant="light">{c.plan?.name ?? c.plan?.code ?? '—'}</Badge></Table.Td>
                    <Table.Td ta="right">{months}</Table.Td>
                    <Table.Td>{c.cancelledAt ? dayjs(c.cancelledAt).format('DD/MM/YYYY') : '—'}</Table.Td>
                    <Table.Td><Text size="sm" c="dimmed">{c.cancellationReason ?? '—'}</Text></Table.Td>
                  </Table.Tr>
                );
              })}
              {snap.churnedRecent.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={5}>
                    <Text c="dimmed" ta="center" py="lg" size="sm">
                      Nessun churn registrato negli ultimi 30 giorni.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      </Card>

      <Card withBorder padding={0} radius="md">
        <Group justify="space-between" p="md" pb="xs">
          <Text fw={600}>Eventi licensing recenti</Text>
          <Badge variant="light" color="gray">{historyQ.data?.data?.length ?? 0}</Badge>
        </Group>
        <ScrollArea type="auto">
          <Table verticalSpacing="sm" horizontalSpacing="md">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Data</Table.Th>
                <Table.Th>Tenant</Table.Th>
                <Table.Th>Azione</Table.Th>
                <Table.Th>Dettagli</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {historyQ.isLoading && (
                <Table.Tr>
                  <Table.Td colSpan={4}>
                    <Stack gap="xs" py="xs">
                      {Array.from({ length: 3 }, (_, i) => <Skeleton key={i} height={18} radius="sm" />)}
                    </Stack>
                  </Table.Td>
                </Table.Tr>
              )}
              {!historyQ.isLoading && (historyQ.data?.data ?? []).slice(0, 30).map((h) => (
                <Table.Tr key={h.id}>
                  <Table.Td>{h.createdAt ? dayjs(h.createdAt).format('DD/MM/YYYY HH:mm') : '—'}</Table.Td>
                  <Table.Td><Text size="sm">{h.tenant?.name ?? h.tenant?.id ?? '—'}</Text></Table.Td>
                  <Table.Td><Badge size="xs" variant="light">{h.action ?? '—'}</Badge></Table.Td>
                  <Table.Td><Text size="xs" c="dimmed">{h.details ?? '—'}</Text></Table.Td>
                </Table.Tr>
              ))}
              {!historyQ.isLoading && (historyQ.data?.data ?? []).length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={4}>
                    <Text c="dimmed" ta="center" py="lg" size="sm">Nessun evento.</Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      </Card>
    </Stack>
  );
}

function Kpi({
  label, value, delta, deltaPct, deltaInverted, icon: Icon, color,
}: {
  label: string;
  value: React.ReactNode;
  delta?: number;
  deltaPct?: number;
  deltaInverted?: boolean;
  icon: typeof IconCash;
  color: string;
}) {
  const sign = deltaPct != null ? Math.sign(deltaPct) : delta != null ? Math.sign(delta) : 0;
  const positive = deltaInverted ? sign < 0 : sign > 0;
  const negative = deltaInverted ? sign > 0 : sign < 0;
  const DeltaIcon = sign === 0 ? IconArrowRight : positive ? IconArrowUpRight : IconArrowDownRight;
  const deltaColor = sign === 0 ? 'gray' : positive ? 'green' : negative ? 'red' : 'gray';

  return (
    <Card withBorder padding="lg" radius="md">
      <Group justify="space-between" mb={4}>
        <Text size="xs" c="dimmed" tt="uppercase" fw={600}>{label}</Text>
        <ThemeIcon size="md" radius="md" variant="light" color={color}>
          <Icon size={16} />
        </ThemeIcon>
      </Group>
      <Title order={2}>{value}</Title>
      {(delta != null || deltaPct != null) && (
        <Badge variant="light" color={deltaColor} size="sm" mt={4} leftSection={<DeltaIcon size={12} />}>
          {deltaPct != null
            ? `${deltaPct > 0 ? '+' : ''}${deltaPct.toFixed(1)}%`
            : `${delta! > 0 ? '+' : ''}${delta}`}
        </Badge>
      )}
    </Card>
  );
}
